import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useCampaigns, useCreateCampaign, usePromotions } from '../../hooks';
import { formatDate } from '../../lib/format';
import type { ToastSeverity } from '../../lib/useToast';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import { CAMPAIGN_STATUS_COLOR, PROMOTION_STATUS_COLOR, statusColorHex } from '../../model/statuses';
import type { CampaignStatus } from '../../model/types';
import { CampaignFormDialog } from './CampaignFormDialog';

const ALL_CAMPAIGN_STATUSES: CampaignStatus[] = ['DRAFT', 'SCHEDULED', 'ACTIVE', 'PAUSED', 'ENDED', 'ARCHIVED'];

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Day-of-month index (1-based), clipped to the visible range; null if entirely outside it. */
function overlapRange(
  start: Date | null,
  end: Date | null,
  monthStart: Date,
  monthEnd: Date,
): { from: number; to: number; overflowsStart: boolean; overflowsEnd: boolean } | null {
  const effectiveStart = start ?? monthStart;
  const effectiveEnd = end ?? monthEnd;
  if (effectiveEnd < monthStart || effectiveStart > monthEnd) return null;
  const clippedStart = effectiveStart < monthStart ? monthStart : effectiveStart;
  const clippedEnd = effectiveEnd > monthEnd ? monthEnd : effectiveEnd;
  return {
    from: clippedStart.getDate(),
    to: clippedEnd.getDate(),
    overflowsStart: effectiveStart < monthStart,
    overflowsEnd: effectiveEnd > monthEnd,
  };
}

export interface CampaignCalendarPanelProps {
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** "Calendar" — campaign month timeline (plan-011 §8.1). */
export function CampaignCalendarPanel({ onResult }: CampaignCalendarPanelProps) {
  const { t } = useTranslation('promotions');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { canManage } = usePromoPermissions();

  const [monthCursor, setMonthCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [statusFilter, setStatusFilter] = useState<Set<CampaignStatus>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);

  const campaigns = useCampaigns({ size: 200 });
  const promotions = usePromotions({ size: 200 }, { enabled: expanded.size > 0 });
  const createCampaign = useCreateCampaign();

  const monthStart = monthCursor;
  const monthEnd = useMemo(
    () => new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0),
    [monthCursor],
  );
  const totalDays = daysInMonth(monthCursor.getFullYear(), monthCursor.getMonth());
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === monthCursor.getFullYear() && today.getMonth() === monthCursor.getMonth();

  const visibleCampaigns = useMemo(() => {
    const all = campaigns.data?.content ?? [];
    return all
      .filter((c) => statusFilter.size === 0 || statusFilter.has(c.status))
      .map((c) => ({
        campaign: c,
        range: overlapRange(
          c.startsAt ? new Date(c.startsAt) : null,
          c.endsAt ? new Date(c.endsAt) : null,
          monthStart,
          monthEnd,
        ),
      }))
      .filter((entry) => entry.range !== null);
  }, [campaigns.data, statusFilter, monthStart, monthEnd]);

  const toggleStatus = (status: CampaignStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const monthLabel = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(monthCursor);

  if (campaigns.isLoading) return <LoadingState label={t('campaignCalendar.loading')} />;
  if (campaigns.isError) {
    return (
      <ErrorState
        title={t('campaignCalendar.loadError.title')}
        message={campaigns.error?.message ?? t('common:error.unknown')}
        onRetry={() => void campaigns.refetch()}
      />
    );
  }

  const hasAnyCampaigns = (campaigns.data?.content.length ?? 0) > 0;

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            size="small"
            aria-label={t('campaignCalendar.previousMonth')}
            onClick={() => setMonthCursor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 160, textAlign: 'center', textTransform: 'capitalize' }}>
            {monthLabel}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('campaignCalendar.nextMonth')}
            onClick={() => setMonthCursor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
          <Button
            size="small"
            variant="text"
            disabled={isCurrentMonth}
            onClick={() => {
              const now = new Date();
              setMonthCursor(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
          >
            {t('campaignCalendar.today')}
          </Button>
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {ALL_CAMPAIGN_STATUSES.map((s) => (
            <Chip
              key={s}
              size="small"
              label={s}
              color={statusFilter.has(s) ? CAMPAIGN_STATUS_COLOR[s] : 'default'}
              variant={statusFilter.has(s) ? 'filled' : 'outlined'}
              onClick={() => toggleStatus(s)}
            />
          ))}
        </Stack>

        <Box sx={{ flex: 1 }} />

        {canManage ? (
          <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)}>
            {t('campaignCalendar.newCampaign')}
          </Button>
        ) : null}
      </Stack>

      {!hasAnyCampaigns ? (
        <EmptyState
          icon={<CalendarMonthRoundedIcon sx={{ fontSize: 48 }} />}
          title={t('campaignCalendar.empty.title')}
          description={t('campaignCalendar.empty.description')}
          action={
            canManage ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)}>
                {t('campaignCalendar.newCampaign')}
              </Button>
            ) : undefined
          }
        />
      ) : isDesktop ? (
        <Card variant="outlined" sx={{ overflowX: 'auto', p: 2 }}>
          {visibleCampaigns.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {t('campaignCalendar.noneThisMonth')}
            </Typography>
          ) : (
            <Box sx={{ minWidth: 200 + totalDays * 28 }}>
              {/* Day header row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: `200px repeat(${totalDays}, minmax(24px, 1fr))`, mb: 1 }}>
                <Box />
                {Array.from({ length: totalDays }, (_, i) => i + 1).map((day) => (
                  <Box
                    key={day}
                    sx={{
                      textAlign: 'center',
                      fontSize: 11,
                      color: 'text.secondary',
                      bgcolor: isCurrentMonth && day === today.getDate() ? alpha(theme.palette.primary.main, 0.06) : undefined,
                    }}
                  >
                    {day}
                  </Box>
                ))}
              </Box>

              {visibleCampaigns.map(({ campaign, range }) => {
                if (!range) return null;
                const color = CAMPAIGN_STATUS_COLOR[campaign.status];
                const isExpanded = expanded.has(campaign.id);
                const campaignPromotions = (promotions.data?.content ?? []).filter((p) => p.campaignId === campaign.id);

                return (
                  <Box key={campaign.id}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `200px repeat(${totalDays}, minmax(24px, 1fr))`,
                        alignItems: 'center',
                        minHeight: 32,
                      }}
                    >
                      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0, pr: 1 }}>
                        <IconButton
                          size="small"
                          aria-expanded={isExpanded}
                          aria-label={t('campaignCalendar.expandAria', { name: campaign.name })}
                          onClick={() =>
                            setExpanded((prev) => {
                              const next = new Set(prev);
                              if (next.has(campaign.id)) next.delete(campaign.id);
                              else next.add(campaign.id);
                              return next;
                            })
                          }
                          sx={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                        >
                          <ExpandMoreRoundedIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title={campaign.name}>
                          <Typography variant="body2" noWrap>
                            {campaign.name}
                          </Typography>
                        </Tooltip>
                      </Stack>
                      <Box
                        role="button"
                        tabIndex={0}
                        aria-label={t('campaignCalendar.barAria', {
                          name: campaign.name,
                          start: campaign.startsAt ? formatDate(campaign.startsAt) : '—',
                          end: campaign.endsAt ? formatDate(campaign.endsAt) : '—',
                          status: campaign.status,
                        })}
                        onClick={() => navigate(`/promo/campaigns/${campaign.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') navigate(`/promo/campaigns/${campaign.id}`);
                        }}
                        sx={{
                          gridColumn: `${range.from + 1} / ${range.to + 2}`,
                          height: 24,
                          borderRadius: 999,
                          bgcolor: alpha(statusColorHex(theme, color), 0.18),
                          border: `1px solid ${statusColorHex(theme, color)}`,
                          borderStyle: campaign.status === 'DRAFT' ? 'dashed' : 'solid',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          overflow: 'hidden',
                          '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                        }}
                      >
                        <Typography variant="caption" noWrap sx={{ color: 'text.primary' }}>
                          {range.overflowsStart ? '◄ ' : ''}
                          {campaign.code}
                          {range.overflowsEnd ? ' ►' : ''}
                        </Typography>
                      </Box>
                    </Box>

                    {isExpanded ? (
                      <>
                        {campaignPromotions.length === 0 ? (
                          <Box sx={{ display: 'grid', gridTemplateColumns: `200px repeat(${totalDays}, minmax(24px, 1fr))` }}>
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 4, py: 0.5 }}>
                              {t('campaignCalendar.noPromotionsYet')}
                            </Typography>
                          </Box>
                        ) : (
                          campaignPromotions.map((promo) => {
                            const promoRange = overlapRange(
                              promo.validFrom ? new Date(promo.validFrom) : null,
                              promo.validTo ? new Date(promo.validTo) : null,
                              monthStart,
                              monthEnd,
                            );
                            if (!promoRange) return null;
                            const promoColor = PROMOTION_STATUS_COLOR[promo.status];
                            return (
                              <Box
                                key={promo.id}
                                sx={{ display: 'grid', gridTemplateColumns: `200px repeat(${totalDays}, minmax(24px, 1fr))`, alignItems: 'center', minHeight: 24 }}
                              >
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ pl: 4 }}>
                                  {promo.name}
                                </Typography>
                                <Box
                                  role="button"
                                  tabIndex={0}
                                  aria-label={promo.name}
                                  onClick={() => navigate(`/promo/promotions/${promo.id}`)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') navigate(`/promo/promotions/${promo.id}`);
                                  }}
                                  sx={{
                                    gridColumn: `${promoRange.from + 1} / ${promoRange.to + 2}`,
                                    height: 16,
                                    borderRadius: 999,
                                    bgcolor: alpha(statusColorHex(theme, promoColor), 0.18),
                                    border: `1px solid ${statusColorHex(theme, promoColor)}`,
                                    cursor: 'pointer',
                                  }}
                                />
                              </Box>
                            );
                          })
                        )}
                      </>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>
      ) : (
        <List>
          {visibleCampaigns.map(({ campaign }) => (
            <ListItemButton key={campaign.id} onClick={() => navigate(`/promo/campaigns/${campaign.id}`)}>
              <ListItemText
                primary={campaign.name}
                secondary={`${campaign.startsAt ? formatDate(campaign.startsAt) : '—'} – ${campaign.endsAt ? formatDate(campaign.endsAt) : '—'}`}
              />
              <Chip size="small" color={CAMPAIGN_STATUS_COLOR[campaign.status]} label={campaign.status} />
            </ListItemButton>
          ))}
        </List>
      )}

      <CampaignFormDialog
        open={dialogOpen}
        saving={createCampaign.isPending}
        onClose={() => setDialogOpen(false)}
        onSubmit={(body) =>
          createCampaign.mutate(body, {
            onSuccess: () => {
              onResult('success', t('campaignCalendar.created'));
              setDialogOpen(false);
            },
            onError: (e) => onResult('error', e.message),
          })
        }
      />
    </>
  );
}
